import React from "react";
import i18n from "../../../../../i18n/i18n";
import {HttpError} from "../../../shared/httpError/httpError";
import {routingAddresses} from "../../../../../helpers/routing";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../models/httpError";
import {ValidatedValue} from "../../../../../helpers/validatedValue";
import Validator from "../../../../../helpers/validator";
import {errorMessages} from "../../../../../helpers/errorMessage";
import {IUserStore} from "../../../../../administration/stores/userStore";
import "./changePassword.scss";
import {observer} from "mobx-react";
import {storesContext} from "../../../../stores/stores";
import {Api} from "../../../../../helpers/api";
import Notification from "../../../shared/notification/notification";
import {AuthService} from "../../../../services/authService";
import Form from "../../../shared/form/form";
import { Utils } from "../../../../../helpers/utils";
import ChangePasswordForm from "../../../shared/changePasswordForm/changePasswordForm";

interface ChangePasswordState {
    currentPassword: ValidatedValue<string>;
    newPassword: ValidatedValue<string>;
    confirmPassword: ValidatedValue<string>;
    isSubmitButtonBlocked: boolean;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    isUserDataError: boolean;
}

class ChangePassword extends React.Component<any, ChangePasswordState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        const notEmptyValidator = {
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        };

        const passwordValidator = {
            name: "passwordIsCorrect",
            fn: Validator.isCorrectPassword,
            errorMessage: () => `${i18n.t("user.changePassword.errors.incorrectPassword")}`
        };

        this.state = {
            confirmPassword: new ValidatedValue("", [notEmptyValidator]),
            newPassword: new ValidatedValue("", [notEmptyValidator, passwordValidator]),
            currentPassword: new ValidatedValue("", [notEmptyValidator]),
            isSubmitButtonBlocked: false,
            response: undefined,
            isUserDataError: false
        };

        const confirmPasswordValidator = {
            name: "confirmPasswordIsCorrect",
            fn: (value: string) => {
                return value === this.state.newPassword.value;
            },
            errorMessage: () => `${i18n.t("user.changePassword.errors.incorrectConfirmPassword")}`
        };
        this.state.confirmPassword.addValidators(confirmPasswordValidator);

        const newPasswordValidator = {
            name: "newPasswordIsCorrect",
            fn: (value: string) => {
                return value !== this.state.currentPassword.value;
            },
            errorMessage: () => `${i18n.t("user.changePassword.errors.incorrectNewPassword")}`
        };
        this.state.newPassword.addValidators(newPasswordValidator);
    }

    render() {
        return (
            <div className="change-password content">
                {this.renderNotification()}
                <div className="header">
                    {i18n.t("user.changePassword.header")}
                </div>
                <div className="username-and-password-header">
                    {i18n.t("user.changePassword.userAndPasswordHeader")}
                </div>
                <Form
                    renderContent={this.renderContent()}
                    isSubmitButtonBlocked={this.state.isSubmitButtonBlocked}
                    backAddress={routingAddresses.administration.list}
                    submit={this.submit.bind(this)}
                />
            </div>
        );
    }

    protected renderContent() {
        const userStore = this.context.userStore as IUserStore;

        return (
            <ChangePasswordForm
                isAdminMode={false}
                currentPassword={this.state.currentPassword}
                newPassword={this.state.newPassword}
                confirmPassword={this.state.confirmPassword}
                username={new ValidatedValue<string>(userStore.username)}
            />
        );
    }

    private renderNotification() {
        if (this.state.response) {
            if (Api.isSuccess(this.state.response)) {
                return (
                    <Notification
                        message={
                            <>
                                <b>{i18n.t("user.changePassword.successFirstPart")}</b>
                                {i18n.t("user.changePassword.successSecondPart")}
                            </>
                        }
                        type="success"
                        onClose={() => {
                            this.setState({response: undefined});
                        }}
                    />
                );
            } else {
                return (
                    <HttpError
                        response={this.state.response}
                        cause={i18n.t("user.changePassword.errors.changePasswordErrorSuffix")}
                        translatedFields={{
                            password: i18n.t("user.changePassword.currentPassword")
                        }}
                    />
                );
            }
        } else {
            return null;
        }
    }

    private get isValid() {
        this.state.newPassword.validate();
        this.state.currentPassword.validate();
        this.state.confirmPassword.validate();

        return this.state.newPassword.isValid && this.state.currentPassword.isValid && this.state.confirmPassword.isValid;
    }

    private async submit() {
        this.setState({response: undefined});

        if (this.isValid) {
            this.setState({isSubmitButtonBlocked: true});
            await this.changeUserPassword();
            this.setState({isSubmitButtonBlocked: false});
        }
        Utils.scrollToFirstErrorMessage();
    }

    private async changeUserPassword() {
        this.setState({
            response: await AuthService.changePassword({
                currentPassword: this.state.currentPassword.value,
                newPassword: this.state.newPassword.value,
                confirmPassword: this.state.confirmPassword.value
            })
        });
    }
}

export default observer(ChangePassword);
